import axios from 'axios';
import VueAxios from 'vue-axios';
import useNotifications from '@/composable/notifications';

export const BASE_URL = process.env.VUE_APP_URL || (
    process.env.NODE_ENV !== 'production'
      ? 'https://tornet.staging.my-tracker.com:1337/api'
      : window.location.origin + ':1337/api'
);

/**
 * Service to call HTTP request via Axios
 */
const ApiService = {

  init(app) {
    app.use(VueAxios, axios);
    axios.defaults.baseURL = BASE_URL;
    // axios.defaults.baseURL = "https://tracker.tornest.net:1337/api";
    axios.defaults.headers.common['Access-Control-Allow-Origin'] = `*`;
    axios.defaults.headers.common['Access-Control-Allow-methods'] = `*`;
    this.setHeader();
  },

  /**
   * Set the default HTTP request headers
   */
  setHeader() {
    axios.interceptors.request.use((config) => {
      const accessToken = localStorage.getItem('access_token');
      if (accessToken) {
        config.headers['Authorization'] = `Bearer ${accessToken}`;
      }
      return config;
    });
    axios.interceptors.response.use(
      (response) => {
        const { hasUnreadMsg, notifyUnreadCount } = useNotifications();

        // You can extract specific headers if needed
        if (response.headers['x-unread-notifications'] !== undefined) {
          notifyUnreadCount.value = response.headers['x-unread-notifications'];
          hasUnreadMsg.value = response.headers['x-unread-notifications'] > 0;
        }

        // Backend sometimes returns 200 with { status: 'error', message: '...' }.
        // Convert that into a rejected promise so callers can handle it in `catch` and show backend message.
        const payloadStatus = response?.data?.status;
        if (typeof payloadStatus === 'string' && payloadStatus.toLowerCase() === 'error') {
          const pseudoError = new Error(response?.data?.message || 'Request failed.');
          pseudoError.response = response;
          pseudoError.isAxiosError = true;
          return Promise.reject(pseudoError);
        }

        return response; // Ensure the response is returned
      },
      (error) => {
        // Handle response errors
        console.error('Error Response:', error.response);
        return Promise.reject(error);
      }
    );
  },

  query(resource, params) {
    return axios.get(resource, params).catch((error) => Promise.reject(error));
  },

  /**
   * Send the GET HTTP request
   * @param resource
   * @param slug
   * @returns {*}
   */
  get(resource) {
    return axios.get(`${resource}`).catch((error) => Promise.reject(error));
  },

  /**
   * Set the POST HTTP request
   * @param resource
   * @param params
   * @param isFormData
   * @param config
   * @returns {*}
   */
  post(resource, params, isFormData = false, config = {}) {
    if (!isFormData) return axios.post(`${resource}`, params);
    else {
      const formData = this.convertObjectToFormData(params);
      return axios.post(`${resource}`, formData, config);
    }
  },

  /**
   * Send the UPDATE HTTP request
   * @param resource
   * @param slug
   * @param params
   * @returns {IDBRequest<IDBValidKey> | Promise<void>}
   */
  update(resource, slug, params) {
    return axios.put(`${resource}/${slug}`, params);
  },

  /**
   * Send the PUT HTTP request
   * @param resource
   * @param params
   * @param isFormData
   * @param config
   * @returns {IDBRequest<IDBValidKey> | Promise<void>}
   */

  put(resource, params, isFormData = false, config = {}) {
    if (!isFormData) return axios.put(`${resource}`, params, config);
    else {
      const formData = this.convertObjectToFormData(params);
      return axios.put(`${resource}`, formData, config);
    }
  },

  patch(resource, params, config = {}) {
    return axios.patch(`${resource}`, params, config);
  },

  /**
   * Send the DELETE HTTP request
   * @param resource
   * @returns {*}
   */
  delete(resource) {
    return axios.delete(resource).catch((error) => Promise.reject(error));
  },

  convertObjectToFormData(obj, formData = new FormData(), parentKey = '') {
    if (obj && typeof obj === 'object' && !(obj instanceof Date) && !(obj instanceof File)) {
      Object.keys(obj).forEach((key) => {
        this.convertObjectToFormData(obj[key], formData, parentKey ? `${parentKey}[${key}]` : key);
      });
    } else {
      if (obj != null) formData.append(parentKey, obj);
    }
    return formData;
  }
};

export default ApiService;
